const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_NEWS_FORM = {
  title: "",
  content: "",
  image_url: "",
  imageFile: null,
  is_active: false,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncateText = (value = "", limit = 160) => {
  const text = stripHtml(value);

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const getArticleDate = (article) =>
  article?.created_at || article?.createdAt || article?.updated_at;

export const getStatusLabel = (isActive) => (isActive ? "Actif" : "Inactif");

export const getStatusClass = (isActive) =>
  isActive
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

export const filterAndSortNews = (news = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const status = filters.status || "all";

  let result = [...news];

  if (status === "active") {
    result = result.filter((article) => Boolean(article.is_active));
  }

  if (status === "inactive") {
    result = result.filter((article) => !Boolean(article.is_active));
  }

  if (query) {
    result = result.filter((article) => {
      const haystack = normalizeText(
        [article.title, stripHtml(article.content)].filter(Boolean).join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "created_asc") {
      return new Date(getArticleDate(a) || 0) - new Date(getArticleDate(b) || 0);
    }

    if (filters.sortOrder === "title_asc") {
      return normalizeText(a.title).localeCompare(normalizeText(b.title));
    }

    if (filters.sortOrder === "title_desc") {
      return normalizeText(b.title).localeCompare(normalizeText(a.title));
    }

    return new Date(getArticleDate(b) || 0) - new Date(getArticleDate(a) || 0);
  });

  return result;
};

export const buildActualitesStats = (news = [], filteredNews = []) => {
  const active = news.filter((article) => Boolean(article.is_active)).length;
  const inactive = news.length - active;

  const withImage = news.filter((article) => Boolean(article.image_url)).length;

  const withContent = news.filter((article) =>
    Boolean(stripHtml(article.content))
  ).length;

  return {
    total: news.length,
    filtered: filteredNews.length,
    active,
    inactive,
    withImage,
    withContent,
  };
};

export const normalizeNewsPayload = (formData) => ({
  ...formData,
  title: String(formData.title || "").trim(),
  content: String(formData.content || "").trim(),
  image_url: String(formData.image_url || "").trim(),
  is_active: Boolean(formData.is_active),
});

export const validateNewsForm = (formData) => {
  if (!String(formData.title || "").trim()) {
    return "Le titre de l'actualité est obligatoire.";
  }

  if (!stripHtml(formData.content)) {
    return "Le contenu de l'actualité est obligatoire.";
  }

  return null;
};

export const buildInitialFormFromArticle = (article) => {
  if (!article) return INITIAL_NEWS_FORM;

  return {
    ...INITIAL_NEWS_FORM,
    ...article,
    imageFile: null,
    is_active: Boolean(article.is_active),
  };
};